import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Save, Play, Bot } from 'lucide-react';
import toast from 'react-hot-toast';
import StrategySetup from './StrategySetup';

type BotSettings = {
  max_position_size: number;
  leverage: number;
  stop_loss_percentage: number;
  take_profit_percentage: number;
  max_daily_trades: number;
  trading_timeframes: string[];
  trailing_stop: {
    enabled: boolean;
    type: 'Distance' | 'Rate';
    value: number;
    activation_price?: number;
  };
  orderBook: {
    useDepth: boolean;
    depthLevel: number; // 1-5 for Bid1/Ask1 to Bid5/Ask5
    side: 'Bid' | 'Ask' | 'Mid';
  };
};

type TradingBot = {
  id: string;
  name: string;
  settings: BotSettings;
  show_on_public: boolean;
  trading_account?: {
    exchange: string;
    api_key: string;
    api_secret: string;
    webhook_url?: string;
  };
};

type TradingPair = {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  is_active: boolean;
};

interface BotSettingsPanelProps {
  bot: TradingBot;
  onClose?: () => void;
}

const defaultSettings: BotSettings = {
  max_position_size: 100,
  leverage: 1,
  stop_loss_percentage: 2,
  take_profit_percentage: 4,
  max_daily_trades: 10,
  trading_timeframes: ['1h'],
  trailing_stop: {
    enabled: false,
    type: 'Distance',
    value: 0
  },
  orderBook: {
    useDepth: false,
    depthLevel: 3, // Default to Bid3/Ask3
    side: 'Mid'
  }
};

function BotSettingsPanel({ bot, onClose }: BotSettingsPanelProps) {
  const [settings, setSettings] = useState<BotSettings>(() => ({
    ...defaultSettings,
    ...bot.settings,
    trailing_stop: {
      ...defaultSettings.trailing_stop,
      ...bot.settings?.trailing_stop
    },
    orderBook: {
      ...defaultSettings.orderBook,
      ...bot.settings?.orderBook
    }
  }));

  const [showOnPublic, setShowOnPublic] = useState(bot.show_on_public || false);
  const [tradingAccount, setTradingAccount] = useState(() => ({
    ...defaultTradingAccount,
    ...(bot.trading_account || {})
  }));
  const [showStrategy, setShowStrategy] = useState(false);
  const [tradingPairs, setTradingPairs] = useState<TradingPair[]>([]);
  const [selectedPairs, setSelectedPairs] = useState<string[]>([]);

  useEffect(() => {
    loadTradingPairs();
    loadSelectedPairs();
  }, [bot.id]);

  const loadTradingPairs = async () => {
    try {
      const { data, error } = await supabase
        .from('trading_pairs')
        .select('*')
        .eq('is_active', true)
        .order('symbol');

      if (error) throw error;
      setTradingPairs(data || []);
    } catch (error) {
      console.error('Error loading trading pairs:', error);
    }
  };

  const loadSelectedPairs = async () => {
    try {
      const { data, error } = await supabase
        .from('bot_trading_pairs')
        .select('pair_symbol')
        .eq('bot_id', bot.id);

      if (error) throw error;
      setSelectedPairs(data?.map(p => p.pair_symbol) || []);
    } catch (error) {
      console.error('Error loading selected pairs:', error);
    }
  };

  const handleSettingChange = (key: keyof BotSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleTrailingStopChange = (key: keyof typeof settings.trailing_stop, value: any) => {
    setSettings(prev => ({
      ...prev,
      trailing_stop: {
        ...prev.trailing_stop,
        [key]: key === 'value' || key === 'activation_price' 
          ? (value === '' ? 0 : parseFloat(value) || 0)
          : value
      }
    }));
  };

  const handleSaveSettings = async () => {
    try {
      // Update bot settings
      const { error: settingsError } = await supabase
        .from('trading_bots')
        .update({ 
          settings,
          show_on_public: showOnPublic,
          trading_account: tradingAccount,
          updated_at: new Date().toISOString()
        })
        .eq('id', bot.id);

      if (settingsError) throw settingsError;

      // Update trading pairs
      // First delete existing pairs
      const { error: deleteError } = await supabase
        .from('bot_trading_pairs')
        .delete()
        .eq('bot_id', bot.id);

      if (deleteError) throw deleteError;

      // Then insert new pairs
      if (selectedPairs.length > 0) {
        const { error: insertError } = await supabase
          .from('bot_trading_pairs')
          .insert(
            selectedPairs.map(symbol => ({
              bot_id: bot.id,
              pair_symbol: symbol
            }))
          );

        if (insertError) throw insertError;
      }

      toast.success('Bot settings updated successfully');
      onClose?.();
    } catch (error: any) {
      toast.error('Failed to update bot settings');
      console.error('Error:', error.message);
    }
  };

  const renderTradingPairsSection = () => (
    <div>
      <label className="block text-sm font-medium text-gray-200 mb-2">
        Trading Pairs
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* USDT Pairs */}
        <div className="bg-black/20 rounded-lg p-4">
          <h4 className="text-sm font-medium text-cyan-400 mb-3">USDT Pairs</h4>
          <div className="space-y-2">
            {tradingPairs
              .filter(pair => pair.quote_asset === 'USDT')
              .map((pair) => (
                <label key={pair.symbol} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedPairs.includes(pair.symbol)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPairs(prev => [...prev, pair.symbol]);
                      } else {
                        setSelectedPairs(prev => prev.filter(p => p !== pair.symbol));
                      }
                    }}
                    className="h-4 w-4 text-cyan-500"
                  />
                  <span className="text-white">{pair.base_asset}/USDT</span>
                </label>
              ))}
          </div>
        </div>

        {/* USD.P Pairs */}
        <div className="bg-black/20 rounded-lg p-4">
          <h4 className="text-sm font-medium text-cyan-400 mb-3">USD.P Pairs</h4>
          <div className="space-y-2">
            {tradingPairs
              .filter(pair => pair.quote_asset === 'USD')
              .map((pair) => (
                <label key={pair.symbol} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedPairs.includes(pair.symbol)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPairs(prev => [...prev, pair.symbol]);
                      } else {
                        setSelectedPairs(prev => prev.filter(p => p !== pair.symbol));
                      }
                    }}
                    className="h-4 w-4 text-cyan-500"
                  />
                  <span className="text-white">{pair.base_asset}/USD.P</span>
                </label>
              ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (showStrategy) {
    return <StrategySetup botId={bot.id} onClose={() => setShowStrategy(false)} />;
  }

  return (
    <div className="mt-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">Bot Settings</h3>
        <button
          onClick={() => setShowStrategy(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
        >
          <Bot className="h-5 w-5" />
          <span>Strategy Setup</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Trading Pairs Section */}
        {renderTradingPairsSection()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Max Position Size (USDT)
            </label>
            <input
              type="number"
              value={settings.max_position_size}
              onChange={(e) => handleSettingChange('max_position_size', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              min="0"
              step="10"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Leverage
            </label>
            <input
              type="number"
              value={settings.leverage}
              onChange={(e) => handleSettingChange('leverage', parseFloat(e.target.value) || 1)}
              className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              min="1"
              max="100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Stop Loss (%)
            </label>
            <input
              type="number"
              value={settings.stop_loss_percentage}
              onChange={(e) => handleSettingChange('stop_loss_percentage', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              min="0"
              step="0.1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Take Profit (%)
            </label>
            <input
              type="number"
              value={settings.take_profit_percentage}
              onChange={(e) => handleSettingChange('take_profit_percentage', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              min="0"
              step="0.1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Max Daily Trades
            </label>
            <input
              type="number"
              value={settings.max_daily_trades}
              onChange={(e) => handleSettingChange('max_daily_trades', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Trading Timeframes
            </label>
            <select
              multiple
              value={settings.trading_timeframes}
              onChange={(e) => handleSettingChange('trading_timeframes',
                Array.from(e.target.selectedOptions, option => option.value)
              )}
              className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              size={4}
            >
              <option value="1m">1 minute</option>
              <option value="5m">5 minutes</option>
              <option value="15m">15 minutes</option>
              <option value="1h">1 hour</option>
              <option value="4h">4 hours</option>
              <option value="1d">1 day</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-200">
                Trailing Stop
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={settings.trailing_stop.enabled}
                  onChange={(e) => handleTrailingStopChange('enabled', e.target.checked)}
                  className="h-4 w-4 text-cyan-500"
                />
                <span className="text-sm text-gray-200">Enable Trailing Stop</span>
              </div>
            </div>

            {settings.trailing_stop.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Type
                  </label>
                  <select
                    value={settings.trailing_stop.type}
                    onChange={(e) => handleTrailingStopChange('type', e.target.value)}
                    className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                  >
                    <option value="Distance">Fixed Distance</option>
                    <option value="Rate">Percentage</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    {settings.trailing_stop.type === 'Distance' ? 'Distance (USDT)' : 'Rate (%)'}
                  </label>
                  <input
                    type="number"
                    value={settings.trailing_stop.value || 0}
                    onChange={(e) => handleTrailingStopChange('value', e.target.value)}
                    className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                    min="0"
                    step={settings.trailing_stop.type === 'Distance' ? '1' : '0.1'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Activation Price (optional)
                  </label>
                  <input
                    type="number"
                    value={settings.trailing_stop.activation_price || ''}
                    onChange={(e) => handleTrailingStopChange('activation_price', e.target.value)}
                    className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                    min="0"
                    step="0.1"
                    placeholder="Enter price..."
                  />
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-200 mb-2">Order Book Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.orderBook.useDepth}
                    onChange={(e) => handleSettingChange('orderBook', {
                      ...settings.orderBook,
                      useDepth: e.target.checked
                    })}
                    className="h-4 w-4 text-cyan-500"
                  />
                  <span className="text-sm text-gray-200">Use Order Book Depth</span>
                </label>
              </div>

              {settings.orderBook.useDepth && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      Depth Level
                    </label>
                    <select
                      value={settings.orderBook.depthLevel}
                      onChange={(e) => handleSettingChange('orderBook', {
                        ...settings.orderBook,
                        depthLevel: parseInt(e.target.value)
                      })}
                      className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                    >
                      {[1, 2, 3, 4, 5].map(level => (
                        <option key={level} value={level}>
                          Bid{level}/Ask{level}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      Price Side
                    </label>
                    <select
                      value={settings.orderBook.side}
                      onChange={(e) => handleSettingChange('orderBook', {
                        ...settings.orderBook,
                        side: e.target.value as 'Bid' | 'Ask' | 'Mid'
                      })}
                      className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                    >
                      <option value="Bid">Bid</option>
                      <option value="Ask">Ask</option>
                      <option value="Mid">Mid</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveSettings}
          className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
        >
          <Save className="h-5 w-5" />
          <span>Save Settings</span>
        </button>
      </div>
    </div>
  );
}

const defaultTradingAccount = {
  exchange: 'binance',
  api_key: '',
  api_secret: '',
  webhook_url: ''
};

export default BotSettingsPanel;
